<!-- Loading -->
<div class="jc-v3-cps-ai-loading" *ngIf="loading">
  <div class="jc-v3-cps-ai-spinner"></div>
  <p>Sumo AI is analyzing the match…</p>
  <small>This can take up to 90 seconds.</small>
</div>

<!-- Error -->
<div class="jc-v3-cps-ai-empty" *ngIf="!loading && loadError">
  <span class="jc-v3-cps-ai-ico jc-v3-cps-ai-ico-warn" aria-hidden="true">⚠</span>
  <p>{{ loadError }}</p>
  <button type="button" class="jc-v3-cps-ai-retry" (click)="lastFetchKey = null; fetch()">Retry</button>
</div>

<!-- Insights body -->
<div class="jc-v3-cps-ai-wrap"
     [class.is-wireframe]="variant === 'wireframe'"
     *ngIf="!loading && !loadError && score !== undefined">

  <!-- Top split — left: recommendation + bars; right: score ring + accuracy -->
  <div class="jc-v3-cps-ai-split">
    <div class="jc-v3-cps-ai-left">
      <div class="jc-v3-cps-ai-title">
        <span class="jc-v3-cps-ai-ico" aria-hidden="true">⚡</span>
        Sumo AI Recommendation
      </div>
      <p class="jc-v3-cps-ai-reason" *ngIf="reason">{{ reason }}</p>

      <!-- Match Breakdown label — only the `wireframe` variant prints it
           (mock spec, `gigsumo_job_center_v2(16).html:1877`). -->
      <div class="jc-v3-cps-ai-bars-label" *ngIf="variant === 'wireframe' && criteria?.length">Match Breakdown</div>

      <!-- Criteria bars — hide any whose regex returned null -->
      <div class="jc-v3-cps-ai-bars">
        <ng-container *ngFor="let c of criteria; trackBy: trackByIndex">
          <div class="jc-v3-cps-ai-bar" *ngIf="c.percentage !== null">
            <div class="jc-v3-cps-ai-bar-head">
              <span class="jc-v3-cps-ai-bar-label">{{ c.label }}</span>
              <span class="jc-v3-cps-ai-bar-pct" [style.color]="barColor(c.label)">{{ c.percentage }}%</span>
            </div>
            <div class="jc-v3-cps-ai-bar-track">
              <div class="jc-v3-cps-ai-bar-fill"
                   [style.width.%]="c.percentage"
                   [style.background]="barColor(c.label)"></div>
            </div>
          </div>
        </ng-container>
      </div>
    </div>

    <div class="jc-v3-cps-ai-right">
      <!-- Default variant draws the score ring around the percentage.
           The `wireframe` variant (mock parity per
           `gigsumo_job_center_v2(16).html:1887-1891`) drops the ring and
           shows the percentage as a single large number, with a small
           up-right arrow next to the "AI Match" label. -->
      <ng-container *ngIf="variant !== 'wireframe'">
        <div class="jc-v3-cps-ai-ring">
          <svg width="68" height="68" viewBox="0 0 68 68">
            <circle cx="34" cy="34" r="28" fill="none" stroke="#F3F4F6" stroke-width="5"/>
            <circle cx="34" cy="34" r="28" fill="none"
                    [attr.stroke]="ringColor" stroke-width="5"
                    stroke-linecap="round"
                    [attr.stroke-dasharray]="ringStroke"
                    transform="rotate(-90 34 34)"/>
          </svg>
          <span class="jc-v3-cps-ai-ring-pct" [style.color]="ringColor">{{ score }}%</span>
        </div>
        <div class="jc-v3-cps-ai-ring-label">AI Match</div>
      </ng-container>
      <ng-container *ngIf="variant === 'wireframe'">
        <div class="jc-v3-cps-ai-pct" [style.color]="ringColor">{{ score }}%</div>
        <div class="jc-v3-cps-ai-ring-label" [style.color]="ringColor">
          <span class="jc-v3-cps-ai-arrow" aria-hidden="true">↗</span>
          AI Match
        </div>
      </ng-container>

      <!-- Overall feedback — ACCURATE / SOMEWHAT_ACCURATE / NOT_ACCURATE.
           Locks after the first pick (buttons disable) and flashes a
           thank-you tooltip — parity with `match-percentage-details`. -->
      <div class="jc-v3-cps-ai-fb">
        <div class="jc-v3-cps-ai-fb-prompt">{{ variant === 'wireframe' ? 'Accurate?' : 'How accurate is this?' }}</div>
        <div class="jc-v3-cps-ai-fb-row">
          <div class="jc-v3-cps-ai-fb-wrap">
            <button type="button"
                    class="jc-v3-cps-ai-fb-opt"
                    [class.active]="overAllFeedback === 'ACCURATE'"
                    (click)="sendOverallFeedback('ACCURATE')">
              <span class="dot" style="background:#10B981"></span> Accurate
              <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingOverall === 'ACCURATE'" aria-hidden="true"></span>
            </button>
          </div>
          <div class="jc-v3-cps-ai-fb-wrap">
            <button type="button"
                    class="jc-v3-cps-ai-fb-opt"
                    [class.active]="overAllFeedback === 'SOMEWHAT_ACCURATE'"
                    (click)="sendOverallFeedback('SOMEWHAT_ACCURATE')">
              <span class="dot" style="background:#F59E0B"></span> Somewhat
              <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingOverall === 'SOMEWHAT_ACCURATE'" aria-hidden="true"></span>
            </button>
          </div>
          <div class="jc-v3-cps-ai-fb-wrap">
            <button type="button"
                    class="jc-v3-cps-ai-fb-opt"
                    [class.active]="overAllFeedback === 'NOT_ACCURATE'"
                    (click)="sendOverallFeedback('NOT_ACCURATE')">
              <span class="dot" style="background:#EF4444"></span> Not accurate
              <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingOverall === 'NOT_ACCURATE'" aria-hidden="true"></span>
            </button>
          </div>
        </div>
        <!-- Per-option confirmation message (wireframe): Thanks! / We'll refine it. / We'll improve. -->
        <div class="jc-v3-cps-ai-fb-msg"
             [style.color]="'#8B5CF6'"
             *ngIf="overallFeedbackMessage">{{ overallFeedbackMessage }}</div>
      </div>
    </div>
  </div>

  <!-- Strengths card — mock parity (gigsumo_job_center_v2(16).html
       lines 1903-1915): bordered card with gray uppercase title +
       Agree/Disagree on the right. Insight icons live on each pill,
       not on the heading. -->
  <div class="jc-v3-cps-card" *ngIf="strengths.length">
    <div class="jc-v3-cps-ai-section-head">
      <h4 class="jc-v3-cps-section-title">Strengths</h4>
      <div class="jc-v3-cps-ai-section-fb">
        <div class="jc-v3-cps-ai-fb-wrap">
          <button type="button"
                  [class.active]="strengthsFeedback === 'AGREE'"
                  (click)="sendCategoryFeedback('STRENGTH', 'AGREE')">
            <span class="jc-v3-cps-ai-ico" aria-hidden="true">👍</span> Agree
            <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingStrength === 'AGREE'" aria-hidden="true"></span>
          </button>
        </div>
        <div class="jc-v3-cps-ai-fb-wrap">
          <button type="button"
                  [class.active]="strengthsFeedback === 'DISAGREE'"
                  (click)="toggleDisagreeInput('strength')">
            <span class="jc-v3-cps-ai-ico" aria-hidden="true">👎</span> Disagree
            <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingStrength === 'DISAGREE'" aria-hidden="true"></span>
          </button>
          <!-- Disagree reason box. `drop-up` flips it above the button when
               there isn't room below inside the scrolling pane. -->
          <div #strengthDisagreeBox
               class="jc-v3-cps-ai-fb-input"
               [class.drop-up]="disagreeDropUp['strength']"
               *ngIf="showDisagreeInput['strength']" [@fadeInOut]>
            <div class="fb-input-head">
              <span>Please provide a reason</span>
              <span class="fb-input-close" (click)="closeDisagreeInput('strength')" aria-hidden="true">✕</span>
            </div>
            <textarea #strengthDisagreeInput
                      [(ngModel)]="disagreeReason['strength']"
                      placeholder="Enter reason for improving the analysis..."
                      rows="3" maxlength="500"
                      (click)="$event.stopPropagation()"></textarea>
            <div class="fb-input-foot">
              <span class="fb-char-count">{{ disagreeReason['strength']?.length || 0 }}/500</span>
              <button type="button" class="fb-submit"
                      (click)="submitDisagree('STRENGTH', 'strength')"
                      [disabled]="!disagreeReason['strength'] || disagreeReason['strength'].trim() === ''">Submit</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ul class="jc-v3-cps-ai-list">
      <li *ngFor="let s of strengths; trackBy: trackByIndex" class="jc-v3-cps-ai-insight-g">
        <span class="jc-v3-cps-ai-ico" aria-hidden="true">✓</span>
        <span class="jc-v3-cps-ai-insight-text">{{ s }}</span>
      </li>
    </ul>
  </div>

  <!-- Gaps & Risks card — same structure as Strengths. -->
  <div class="jc-v3-cps-card" *ngIf="weaknesses.length">
    <div class="jc-v3-cps-ai-section-head">
      <h4 class="jc-v3-cps-section-title">Gaps &amp; Risks</h4>
      <div class="jc-v3-cps-ai-section-fb">
        <div class="jc-v3-cps-ai-fb-wrap">
          <button type="button"
                  [class.active]="weaknessesFeedback === 'AGREE'"
                  (click)="sendCategoryFeedback('WEAKNESS', 'AGREE')">
            <span class="jc-v3-cps-ai-ico" aria-hidden="true">👍</span> Agree
            <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingWeakness === 'AGREE'" aria-hidden="true"></span>
          </button>
        </div>
        <div class="jc-v3-cps-ai-fb-wrap">
          <button type="button"
                  [class.active]="weaknessesFeedback === 'DISAGREE'"
                  (click)="toggleDisagreeInput('weakness')">
            <span class="jc-v3-cps-ai-ico" aria-hidden="true">👎</span> Disagree
            <span class="jc-v3-cps-ai-btn-spin" *ngIf="savingWeakness === 'DISAGREE'" aria-hidden="true"></span>
          </button>
          <!-- Disagree reason box. `drop-up` flips it above the button when
               there isn't room below inside the scrolling pane. -->
          <div #weaknessDisagreeBox
               class="jc-v3-cps-ai-fb-input"
               [class.drop-up]="disagreeDropUp['weakness']"
               *ngIf="showDisagreeInput['weakness']" [@fadeInOut]>
            <div class="fb-input-head">
              <span>Please provide a reason</span>
              <span class="fb-input-close" (click)="closeDisagreeInput('weakness')" aria-hidden="true">✕</span>
            </div>
            <textarea #weaknessDisagreeInput
                      [(ngModel)]="disagreeReason['weakness']"
                      placeholder="Enter reason for improving the analysis..."
                      rows="3" maxlength="500"
                      (click)="$event.stopPropagation()"></textarea>
            <div class="fb-input-foot">
              <span class="fb-char-count">{{ disagreeReason['weakness']?.length || 0 }}/500</span>
              <button type="button" class="fb-submit"
                      (click)="submitDisagree('WEAKNESS', 'weakness')"
                      [disabled]="!disagreeReason['weakness'] || disagreeReason['weakness'].trim() === ''">Submit</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ul class="jc-v3-cps-ai-list">
      <li *ngFor="let w of weaknesses; trackBy: trackByIndex" class="jc-v3-cps-ai-insight-y">
        <span class="jc-v3-cps-ai-ico" aria-hidden="true">⚠</span>
        <span class="jc-v3-cps-ai-insight-text">{{ w }}</span>
      </li>
    </ul>
  </div>
</div>
